import moment from "moment";

export const isValidEmail = (email) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
export const isValidPhone = (phone) =>
  /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/.test(phone);
const isOnlyLettersAndNumbers = (str) => /^[A-Za-z0-9]*$/.test(str);
const isEmptyStringOrNull = (val) => val == null || val === "";
const arrayHasValues = (val) => Array.isArray(val) && val.length > 0;
const isValidDate = (d) => d instanceof Date && !isNaN(d);

// TWDB FP Project ID: Numeric, 9 digits. 2-digit Flood Planning Region identifier; followed by "1" (for FME), "2" (for FMS),
// or "3" (for FMP); followed by 6-digit unique sequence generated by vendor.
export const isValidTwdbFloodPlanningProjectID = (val) =>
  /^(0|1)\d(1|2|3)\d{6}$/.test(val);

const REQUIRED_METADATA_TEXT_FIELDS = [
  "hydrologicUnitCode",
  "modelName",
  "creator",
  "modelDescription",
  "modelPurpose",
  "kewyWordTerm",
  "updateFrequency",
  "useConstraint",
  "modelType",
  "modelCategory",
  "softwareName",
  "softwareVersion",
  "contactName",
  "contactAffiliation",
  "contactEmail",
  "contactPhoneNumber",
  "contactDepartment",
];

const REQUIRED_METADATA_LIST_FIELDS = ["county", "city"];

const addRequiredTextErrors = (errors, values, fields) => {
  fields.forEach((field) => {
    if (isEmptyStringOrNull(values[field])) {
      errors[field] = "Required";
    }
  });
};

const addRequiredListErrors = (errors, values, fields) => {
  fields.forEach((field) => {
    if (!arrayHasValues(values[field])) {
      errors[field] = "Required";
    }
  });
};

const hasBaselineModelValue = (baselineVal) =>
  typeof baselineVal === "string"
    ? baselineVal.trim() !== ""
    : Boolean(baselineVal);

const addConditionalMetadataErrors = (errors, values, isNctcog, isRbfs) => {
  if (!isNctcog && isEmptyStringOrNull(values.rbfsRegionID)) {
    errors.rbfsRegionID = "Required";
  }
  if (
    isNctcog &&
    values.modelType === "Other" &&
    isEmptyStringOrNull(values.otherModelType)
  ) {
    errors.otherModelType = "Required";
  }
  if ((isNctcog || isRbfs) && isEmptyStringOrNull(values.projectName)) {
    errors.projectName = "Required";
  }
  if (
    values.modelCategory === "Alternative Analysis Model" &&
    !hasBaselineModelValue(values.baselineModel)
  ) {
    errors.baselineModel = "Required";
  }
};

const isRecognizedMetadataDate = (value) =>
  isValidDate(value) ||
  moment(value, ["YYYY-MM-DD", moment.ISO_8601], true).isValid();

const addCreatedDateError = (errors, values) => {
  if (isEmptyStringOrNull(values.createdDate)) {
    errors.createdDate = "Required";
    return;
  }
  if (!isRecognizedMetadataDate(values.createdDate)) {
    errors.createdDate = "Invalid date";
  }
};

export const validateMetadata =
  (metadataFieldInfo, { isNctcog = false, isRbfs = false } = {}) =>
  (values) => {
    const errors = {};
    addRequiredTextErrors(errors, values, REQUIRED_METADATA_TEXT_FIELDS);
    addRequiredListErrors(errors, values, REQUIRED_METADATA_LIST_FIELDS);
    addConditionalMetadataErrors(errors, values, isNctcog, isRbfs);
    addCreatedDateError(errors, values);
    return errors;
  };

export const validateExample = (values) => {
  const errors = {};
  if (!values.email) {
    errors.email = "Required";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }
  return errors;
};

export const validateContact =
  (isOriginator, contactsById, isCreatingNew) => (values) => {
    const errors = {};
    if (!values.email) {
      errors.email = "Required";
    } else if (!isValidEmail(values.email)) {
      errors.email = "Invalid email address";
    } else {
      const emailAlreadyExists = Object.values(contactsById).some(
        (contactObj) => contactObj.email === values.email,
      );
      // If not creating new (AKA editing), the email will already exist & is ineditable, so we shouldn't return an error
      if (emailAlreadyExists && isCreatingNew) {
        errors.email =
          "A contact with this email already exists. Please select the contact from existing contacts list instead.";
      }
    }
    if (!values.phoneNumber) {
      errors.phoneNumber = "Required";
    }
    if (!isOriginator && !values.agency) {
      errors.agency = "Required";
    }
    if (!values.firstName) {
      errors.firstName = "Required";
    }
    if (!values.lastName) {
      errors.lastName = "Required";
    }
    return errors;
  };

export const validatePersonalContact = (values) => {
  const errors = {};
  if (!values.phoneNumber) {
    errors.phoneNumber = "Required";
  }
  if (!values.agency) {
    errors.agency = "Required";
  }
  if (!values.firstName) {
    errors.firstName = "Required";
  }
  if (!values.lastName) {
    errors.lastName = "Required";
  }
  return errors;
};

export const validateLogin = (values) => {
  const errors = {};
  if (!values.username) {
    errors.username = "Username is required";
  }
  // } else if (!isValidEmail(values.email)) {
  //   errors.email = 'Invalid email address';
  // }
  if (!values.password) {
    errors.password = "Password is required";
  }
  return errors;
};

export const validateForgotPassword = (values) => {
  const errors = {};
  if (!values.email) {
    errors.email = "Email address is required";
  } else if (!isValidEmail(values.email)) {
    errors.email = "Invalid email address";
  }
  return errors;
};

export const validateResetPassword = (values) => {
  const errors = {};
  if (!values.oldPassword) {
    errors.oldPassword = "Required";
  }

  if (!values.newPassword) {
    errors.newPassword = "Required";
  } else if (!isOnlyLettersAndNumbers(values.newPassword)) {
    errors.newPassword =
      "Due to usage in scripts, passwords can only contain numbers and upper & lowercase letters (certain symbols are restricted)";
  } else if (values.newPassword.length < 10) {
    errors.newPassword = "Invalid password, must be 10 characters or more";
  }
  if (!values.newPasswordConfirmation) {
    errors.newPasswordConfirmation = "Required";
  } else if (values.newPasswordConfirmation !== values.newPassword) {
    errors.newPasswordConfirmation = "Passwords must match";
  }
  return errors;
};
